import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Dropdown, MENU_ID_PREFIX, type DropdownType } from './Dropdown';
import { DropdownSearch, itemSearchText, type DropdownSearchSection } from './DropdownSearch';
import { DropdownItem, type DropdownItemProps } from './DropdownItem';
import { InputMultiSelect } from '../Input/InputMultiSelect';
import { InputSingleSelect } from '../Input/InputSingleSelect';
import { useControllableState } from '../../hooks/useControllableState';
import { useOutsideClick } from '../../hooks/useOutsideClick';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { useAnchoredPanelPosition } from '../../hooks/useAnchoredPanelPosition';
import { useReadOnly } from '../ReadOnly/ReadOnly';
import { useListNavigation } from '../../hooks/useListNavigation';
import { getOverlayPortalParent } from '../../utils/getOverlayPortalParent';
import { fuzzyFilter } from '../../utils/fuzzyFilter';
import type { DropdownMenuSize, DropdownMenuSelectType, DropdownMenuProps } from './DropdownMenu.types';
import './DropdownMenu.css';

const MAX_VISIBLE_ITEMS = 5;

function filterByQuery(items: DropdownItemProps[], query: string, fuzzy: boolean): DropdownItemProps[] {
  if (!query) return items;
  if (fuzzy) return fuzzyFilter(items, query, itemSearchText);
  const lower = query.toLowerCase();
  return items.filter((item) => itemSearchText(item).toLowerCase().includes(lower));
}

export function DropdownMenu({
  open,
  defaultOpen = false,
  onOpenChange,
  panelLayout = 'overlay',
  size = 'medium',
  fill = false,
  selectType = 'single-select',
  label = 'Label',
  placeholder = 'Input',
  text,
  showLabel = true,
  showAsterisk = false,
  showFooter = false,
  footer,
  showPrefixIcon = false,
  prefixIcon,
  showInfoIcon = false,
  tags = [],
  onTagRemove,
  overflowCount = 0,
  disabled = false,
  searchable = false,
  searchMode = 'panel',
  fuzzySearch = false,
  searchPlaceholder,
  searchEmptyText,
  items = [],
  panelTitle,
  sections,
  value,
  onSelect,
  showClearButton = false,
  className = '',
  clearLabel,
  applyLabel,
  onClear,
  onApply,
  clearDisabled,
  applyDisabled,
  visualState,
  showFocus,
  panelClassName = '',
}: DropdownMenuProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const { readOnly } = useReadOnly();

  const [isOpen, setOpen] = useControllableState(open, defaultOpen, onOpenChange);

  const close = useCallback(() => setOpen(false), [setOpen]);

  const isPortaled = panelLayout === 'overlay';

  const panelStyle = useAnchoredPanelPosition(rootRef, panelRef, isOpen && isPortaled);

  // The portaled panel lives outside rootRef in the DOM, so it must be treated
  // as "inside" for outside-click detection to avoid closing before selection.
  useOutsideClick([rootRef, panelRef], close, isOpen);
  useEscapeKey(close, isOpen);

  const dropdownType: DropdownType = selectType === 'multi-select' ? 'checkbox' : 'regular';

  const triggerText = typeof value === 'string' ? value : text;

  const rootClasses = [
    'spark-dropdown-menu',
    `spark-dropdown-menu-${size}`,
    panelLayout === 'overlay' ? 'spark-dropdown-menu-overlay' : 'spark-dropdown-menu-inline',
    isOpen ? 'spark-dropdown-menu-open' : 'spark-dropdown-menu-closed',
    fill ? 'spark-dropdown-menu-fill' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleToggle = () => {
    if (!disabled) {
      setOpen(!isOpen);
    }
  };

  const handleSelect = (item: DropdownItemProps, index: number) => {
    onSelect?.(item, index);
    if (selectType === 'single-select') {
      close();
    }
  };

  const handleTriggerClear = () => {
    close();
    onClear?.();
  };

  const panelClasses = [
    'spark-dropdown-menu-panel',
    isPortaled ? 'spark-dropdown-menu-panel--portal' : '',
    panelClassName,
  ]
    .filter(Boolean)
    .join(' ');

  const useTriggerSearch = searchable && searchMode === 'trigger' && selectType === 'single-select';
  const useSearchPanel = searchable && selectType === 'single-select' && !useTriggerSearch;
  // Plain (non-searchable) single-select: panel rows are mouse-only divs, so
  // keyboard navigation runs while focus stays on the trigger field — arrows
  // move a highlight through the list and Enter commits it.
  const usePlainListNav = selectType === 'single-select' && !searchable;

  // --- Trigger-search (combobox) mode: the query lives in the trigger input,
  // the panel is a plain filtered list. Hooks below are unconditional; they
  // are inert when the mode is off.
  const [query, setQuery] = useState('');
  const itemIdPrefix = useId();

  useEffect(() => {
    if (!isOpen) setQuery('');
  }, [isOpen]);

  const filteredSections = useMemo((): DropdownSearchSection[] => {
    if (!useTriggerSearch) return [];
    if (sections) {
      return sections
        .map((section) => ({ title: section.title, items: filterByQuery(section.items, query, fuzzySearch) }))
        .filter((section) => section.items.length > 0);
    }
    const filtered = filterByQuery(items, query, fuzzySearch);
    return filtered.length > 0 ? [{ title: '', items: filtered }] : [];
  }, [useTriggerSearch, sections, items, query, fuzzySearch]);

  const flatItems = useMemo(
    () => filteredSections.flatMap((section) => section.items),
    [filteredSections],
  );

  // The keyboard highlight walks the filtered list in trigger-search mode and
  // the raw items in plain single-select mode.
  const navItems = useTriggerSearch ? flatItems : items;

  const { activeIndex, setActiveIndex, handleKeyDown: handleListKeyDown } = useListNavigation({
    itemCount: navItems.length,
  });

  // Callers often pass fresh item arrays each render; key off the item text so
  // the keyboard highlight only resets when the visible options actually
  // change. `itemSearchText` reads `searchText` for node labels, which would
  // otherwise all stringify alike and keep a stale highlight after filtering.
  const navItemKey = useMemo(
    () => navItems.map((item) => itemSearchText(item)).join('\0'),
    [navItems],
  );
  useEffect(() => {
    setActiveIndex(-1);
  }, [navItemKey, setActiveIndex]);

  useEffect(() => {
    if (!isOpen) setActiveIndex(-1);
  }, [isOpen, setActiveIndex]);

  // Keep the keyboard-highlighted item visible inside the scrollable list.
  useEffect(() => {
    if (activeIndex < 0 || !(useTriggerSearch || usePlainListNav)) return;
    const id = useTriggerSearch
      ? `${itemIdPrefix}${activeIndex}`
      : `${MENU_ID_PREFIX}${activeIndex}`;
    document.getElementById(id)?.scrollIntoView({ block: 'nearest' });
  }, [useTriggerSearch, usePlainListNav, activeIndex, itemIdPrefix]);

  const isItemDisabled = (item: DropdownItemProps) =>
    item.state === 'disabled' || item.visualState === 'disabled' || (readOnly && Boolean(item.disableWhenReadOnly));

  const selectTriggerSearchItem = (item: DropdownItemProps, index: number) => {
    if (isItemDisabled(item)) return;
    item.onClick?.();
    onSelect?.(item, index);
    setOpen(false);
  };

  // ↑/↓ move the highlight; Enter picks the highlighted item, or the first
  // match while filtering. Enter on an untouched, unfiltered list does
  // nothing so the panel isn't committed by accident.
  const handleTriggerSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Tab') {
      // Tabbing away closes the panel; focus moves on naturally before the
      // search input unmounts, so it is not pulled back to the field.
      close();
      return;
    }
    if (event.key === 'Enter') {
      const index = activeIndex >= 0 ? activeIndex : query ? 0 : -1;
      const item = index >= 0 ? flatItems[index] : undefined;
      if (item) {
        event.preventDefault();
        selectTriggerSearchItem(item, index);
      }
      return;
    }
    handleListKeyDown(event);
  };

  // Trigger-field keys for the plain single-select: ArrowUp/Down open the
  // closed panel; while open, arrows move the highlight, Enter commits the
  // highlighted option (falling through to the default toggle when nothing is
  // highlighted) and Tab closes the panel so the UI follows focus.
  const handlePlainFieldKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!usePlainListNav || disabled) return;
    if (!isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (event.key === 'Tab') {
      close();
      return;
    }
    if (event.key === 'Enter' && activeIndex >= 0) {
      const item = items[activeIndex];
      if (item && !isItemDisabled(item)) {
        event.preventDefault();
        item.onClick?.();
        onSelect?.(item, activeIndex);
        close();
      }
      return;
    }
    handleListKeyDown(event);
  };

  // Mirror the keyboard highlight into the panel rows' hover visual.
  const plainNavItems = useMemo(() => {
    if (!usePlainListNav || activeIndex < 0) return items;
    return items.map((item, index) =>
      index === activeIndex && !item.visualState
        ? { ...item, visualState: 'hover' as const }
        : item,
    );
  }, [usePlainListNav, items, activeIndex]);

  const triggerSearchBlocks = useMemo(() => {
    let offset = 0;
    return filteredSections.map((section) => {
      const start = offset;
      offset += section.items.length;
      return { section, start };
    });
  }, [filteredSections]);

  const triggerSearchList = (
    <div
      className={[
        'spark-dropdown',
        'spark-dropdown-search',
        flatItems.length > MAX_VISIBLE_ITEMS ? 'spark-dropdown-scrollable' : '',
        footer ? 'spark-dropdown-with-footer' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      role="menu"
    >
      <div className="spark-dropdown-body">
        {flatItems.length > 0 ? (
          triggerSearchBlocks.map(({ section, start }) => (
            <div key={section.title || 'default'} className="spark-dropdown-search-section">
              {section.title ? (
                <div className="spark-dropdown-search-section-title" role="presentation">
                  {section.title}
                </div>
              ) : null}
              {section.items.map((item, localIndex) => {
                const index = start + localIndex;
                return (
                  <DropdownItem
                    key={`${section.title}-${itemSearchText(item)}-${index}`}
                    {...item}
                    id={`${itemIdPrefix}${index}`}
                    visualState={item.visualState ?? (index === activeIndex ? 'hover' : undefined)}
                    showPrefixIcon={item.showPrefixIcon ?? showPrefixIcon}
                    showInfoIcon={item.showInfoIcon ?? showInfoIcon}
                    onClick={() => selectTriggerSearchItem(item, index)}
                  />
                );
              })}
            </div>
          ))
        ) : (
          <div className="spark-dropdown-search-empty" role="status">
            {searchEmptyText ?? 'No results'}
          </div>
        )}
      </div>
      {footer != null ? (
        <div
          className="spark-dropdown-custom-footer"
          onMouseDown={(event) => event.stopPropagation()}
        >
          {footer}
        </div>
      ) : null}
    </div>
  );

  const panel = (
    <div ref={panelRef} className={panelClasses} style={isPortaled ? panelStyle : undefined}>
      {useTriggerSearch ? (
        triggerSearchList
      ) : useSearchPanel ? (
        <DropdownSearch
          searchPlaceholder={searchPlaceholder}
          emptyText={searchEmptyText}
          showFooter={showFooter && footer == null}
          footer={footer}
          showPrefixIcon={showPrefixIcon}
          showInfoIcon={showInfoIcon}
          items={items}
          sections={sections}
          fuzzySearch={fuzzySearch}
          onSelect={handleSelect}
          autoFocus
        />
      ) : (
        <Dropdown
          type={dropdownType}
          title={panelTitle}
          showTitle={panelTitle != null}
          showFooter={showFooter}
          footer={footer}
          showPrefixIcon={showPrefixIcon}
          showInfoIcon={showInfoIcon}
          items={usePlainListNav ? plainNavItems : items}
          value={value}
          onSelect={handleSelect}
          clearLabel={clearLabel}
          applyLabel={applyLabel}
          onClear={onClear}
          onApply={onApply}
          clearDisabled={clearDisabled}
          applyDisabled={applyDisabled}
        />
      )}
    </div>
  );

  return (
    <div className={rootClasses} ref={rootRef}>
      {selectType === 'multi-select' ? (
        <InputMultiSelect
          size={size}
          label={label}
          placeholder={placeholder}
          showLabel={showLabel}
          showAsterisk={showAsterisk}
          showPrefixIcon={showPrefixIcon}
          tags={tags}
          onTagRemove={onTagRemove}
          overflowCount={overflowCount}
          disabled={disabled}
          open={isOpen}
          onToggle={handleToggle}
          visualState={visualState}
          showFocus={showFocus}
          fill={tags.length > 0 ? 'filled' : 'empty'}
        />
      ) : (
        <InputSingleSelect
          size={size}
          label={label}
          placeholder={placeholder}
          text={triggerText}
          showLabel={showLabel}
          showAsterisk={showAsterisk}
          showPrefixIcon={showPrefixIcon}
          prefixIcon={prefixIcon}
          disabled={disabled}
          open={isOpen}
          onToggle={handleToggle}
          visualState={visualState}
          showFocus={showFocus}
          fill={triggerText !== undefined && triggerText.length > 0 ? 'filled' : 'empty'}
          showClearButton={showClearButton}
          onClear={handleTriggerClear}
          editable={useTriggerSearch}
          searchValue={query}
          onSearchChange={setQuery}
          onSearchKeyDown={handleTriggerSearchKeyDown}
          onFieldKeyDown={usePlainListNav ? handlePlainFieldKeyDown : undefined}
          activeDescendantId={
            useTriggerSearch && activeIndex >= 0
              ? `${itemIdPrefix}${activeIndex}`
              : usePlainListNav && isOpen && activeIndex >= 0
                ? `${MENU_ID_PREFIX}${activeIndex}`
                : undefined
          }
        />
      )}
      {isOpen ? (isPortaled ? createPortal(panel, getOverlayPortalParent(rootRef.current)) : panel) : null}
    </div>
  );
}
